import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { X, BookOpen } from 'lucide-react';
import { Benefits } from './components/Benefits';
import { BookPreview } from './components/BookPreview';
import { OrderModal } from './components/Order/OrderModal';
import { SocialLinks } from './components/SocialLinks';
import { LiveOrderNotifications } from './components/LiveOrderNotifications';
import { InstagramTestimonials } from './components/InstagramTestimonials';
import { HeroSection } from './components/HeroSection';
import { TrustSection } from './components/TrustSection';
import { FooterSection } from './components/FooterSection';
import { AdvancedSEO } from './components/AdvancedSEO';
import { FAQSection } from './components/FAQSection';
import { PaymentSuccess } from './components/Order/PaymentSuccess';
function MainPage() {
  console.log('MainPage rendered');
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  useEffect(() => {
    console.log('isOrderModalOpen changed:', isOrderModalOpen);
  }, [isOrderModalOpen]);
  const [isExitIntentModalOpen, setIsExitIntentModalOpen] = useState(false);
  const [hasExitIntentTriggered, setHasExitIntentTriggered] = useState(() => {
    try {
      return localStorage.getItem('hasExitIntentTriggered') === 'true';
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return false;
    }
  });
  const [activeReaders, setActiveReaders] = useState(127);

  // Clear localStorage on mount
  useEffect(() => {
    try {
      const itemsToClear = ['timer', 'stockLeft'];
      itemsToClear.forEach(item => {
        if (localStorage.getItem(item)) {
          localStorage.removeItem(item);
        }
      });
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }, []);

  // Active readers counter
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveReaders(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(120, Math.min(150, prev + change));
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Exit intent handling
  const handleExitIntent = () => {
    if (!hasExitIntentTriggered) {
      setIsExitIntentModalOpen(true);
      setHasExitIntentTriggered(true);
      try {
        localStorage.setItem('hasExitIntentTriggered', 'true');
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    }
  };

  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      if (event.clientY < 0) {
        handleExitIntent();
      }
    };
    
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasExitIntentTriggered]);

  const handleOrderNowClick = () => {
    console.log('handleOrderNowClick called');
    setIsOrderModalOpen(true);
    console.log('Setting isOrderModalOpen to true');
    setIsExitIntentModalOpen(false);
  };

  const handleCloseExitIntentModal = () => {
    setIsExitIntentModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      <AdvancedSEO />
      
      <HeroSection 
        activeReaders={activeReaders}
        onOrderClick={handleOrderNowClick}
      />

      <section className="py-16 sm:py-24 bg-gray-900/50 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 sm:mb-16 bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">
            Ce vei descoperi în carte
          </h2>
          <div className="flex justify-center">
            <div className="w-full max-w-6xl">
              <Benefits />
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <button 
              onClick={handleOrderNowClick}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white text-base sm:text-lg px-6 sm:px-8 py-2 sm:py-3 rounded-full font-bold transform transition-all duration-500 hover:scale-105 shadow-[0_0_30px_rgba(239,68,68,0.3)] hover:shadow-[0_0_50px_rgba(239,68,68,0.5)]"
            >
              <span>COMANDĂ ACUM</span> <BookOpen className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <BookPreview onOrderClick={handleOrderNowClick} />
      <InstagramTestimonials onOrderClick={handleOrderNowClick} />
      <TrustSection />
      <FAQSection />
      <SocialLinks />
      <FooterSection />

      <OrderModal isOpen={isOrderModalOpen} onClose={() => setIsOrderModalOpen(false)} />
      <LiveOrderNotifications />

      {/* Exit Intent Modal */}
      {isExitIntentModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg bg-gradient-to-b from-gray-900 to-black rounded-2xl shadow-lg border border-red-500/20">
            <button 
              onClick={handleCloseExitIntentModal}
              className="absolute -top-4 -right-4 w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors shadow-lg"
              aria-label="Închide fereastra"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="p-6">
              <h3 className="text-xl sm:text-2xl font-bold mb-4">Nu plecați încă!</h3>
              <p className="text-gray-300 text-base sm:text-lg">
                Știați că 90% dintre cititorii noștri au găsit inspirație și motivație în această carte? Nu ratați șansa de a vă transforma viața!
              </p>
              <button 
                onClick={handleOrderNowClick}
                className="w-full mt-4 bg-gradient-to-r from-red-600 to-red-700 text-white text-lg sm:text-xl px-8 sm:px-12 py-3 sm:py-4 rounded-full font-bold transform transition-all duration-500 hover:scale-105 flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(239,68,68,0.3)] hover:shadow-[0_0_50px_rgba(239,68,68,0.5)]"
                aria-label="Comandă cartea acum"
              >
                <span>COMANDĂ ACUM</span> <BookOpen className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<MainPage />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/payment-success/:order_id" element={<PaymentSuccess />} />
      <Route path="/payment-success/*" element={<PaymentSuccess />} />
    </Routes>
  );
}

export default App;
